import { useMemo, useCallback, useEffect, useState } from "react";
import { useSeoMeta } from "@unhead/react";
import { useActiveAccount } from "applesauce-react/hooks";
import {
  Globe,
  RefreshCw,
  Users,
  Radio,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { LoginArea } from "@/components/auth/LoginArea";
import { NsiteCard } from "@/components/NsiteCard";
import { GatewaySettingsDialog } from "@/components/GatewaySettingsDialog";
import { useNsiteGateway } from "@/hooks/useNsiteGateway";
import { use$ } from "@/hooks/use$";
import { useUser } from "@/hooks/useUser";
import { useEventStore } from "@/hooks/useEventStore";
import { pool } from "@/services/nostr";
import { nsiteRelays } from "@/services/settings";
import { mapEventsToStore, mapEventsToTimeline } from "applesauce-core";
import { onlyEvents } from "applesauce-relay";
import type { NostrEvent } from "nostr-tools";
import { cn } from "@/lib/utils";

// nsite kinds as per NIP-5A
const NSITE_MANIFEST_KINDS = [15128, 35128];

interface FeedItem {
  key: string;
  event: NostrEvent;
  sourceKind: "zap" | "manifest";
  zapperPubkey?: string;
  timestamp: number;
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl overflow-hidden border border-border/40"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <Skeleton className="h-40 w-full rounded-none" />
          <div className="p-4 space-y-3">
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-4/5" />
            </div>
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-7 w-7 rounded-full" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div className="flex gap-1.5">
                <Skeleton className="h-7 w-16 rounded-md" />
                <Skeleton className="h-7 w-16 rounded-md" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ isLoggedIn }: { isLoggedIn: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="relative mb-6">
        <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center animate-pulse-glow">
          <Globe className="w-9 h-9 text-primary/60" />
        </div>
      </div>
      <h3 className="text-xl font-semibold text-foreground mb-2">
        {isLoggedIn ? "No nsite activity found" : "Discover the nsite ecosystem"}
      </h3>
      <p className="text-muted-foreground max-w-sm text-sm leading-relaxed">
        {isLoggedIn
          ? "Your friends haven't published or zapped any nsites recently. Try following more people or checking back later."
          : "Log in with your Nostr account to see nsite events from your social graph, and zap your favorites to spread the word."}
      </p>
    </div>
  );
}

function Header({
  gateway,
  onGatewayChange,
  feedItemCount,
  isLoading,
  onRefresh,
}: {
  gateway: string;
  onGatewayChange: (g: string) => void;
  feedItemCount: number;
  isLoading: boolean;
  onRefresh: () => void;
}) {
  return (
    <header className="sticky top-0 z-30 border-b border-border/40 bg-background/80 backdrop-blur-xl">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center ring-1 ring-primary/30">
            <Globe className="w-4 h-4 text-primary" />
          </div>
          <div className="hidden sm:block">
            <h1 className="text-base font-bold leading-none gradient-text">
              nsite.feed
            </h1>
            <p className="text-[10px] text-muted-foreground leading-none mt-0.5">
              Nostr static sites
            </p>
          </div>
        </div>

        {/* Center: feed stats */}
        <div className="flex items-center gap-2">
          {feedItemCount > 0 && (
            <Badge
              variant="secondary"
              className="bg-secondary/80 text-muted-foreground text-xs"
            >
              <Radio className="w-2.5 h-2.5 mr-1.5 text-green-400 animate-pulse" />
              {feedItemCount} sites
            </Badge>
          )}
        </div>

        {/* Right: controls */}
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 text-muted-foreground hover:text-foreground"
            onClick={onRefresh}
            disabled={isLoading}
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <GatewaySettingsDialog
            gateway={gateway}
            onGatewayChange={onGatewayChange}
          />
          <LoginArea className="max-w-52" />
        </div>
      </div>
    </header>
  );
}

// Hook to fetch contacts' nsite events + zaps
function useFriendNsiteFeed(
  pubkey: string | undefined,
): { items: FeedItem[]; isLoading: boolean; refresh: () => void } {
  const store = useEventStore();
  const user = useUser(pubkey);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const relays = use$(nsiteRelays) ?? [];

  // Get the follows list
  const follows = use$(() => user?.follows$, [user?.pubkey]);

  const followPubkeys = useMemo(() => {
    if (!follows) return [];
    return follows.slice(0, 200); // limit to avoid huge queries
  }, [follows]);

  const contactKey = useMemo(() => JSON.stringify(followPubkeys), [followPubkeys]);
  const relayKey = useMemo(() => JSON.stringify(relays), [relays]);

  // Fetch nsite manifests from contacts
  const manifestEvents = use$(
    () => {
      if (!pubkey || relays.length === 0) return undefined;

      const authors = followPubkeys.length > 0 ? followPubkeys : [pubkey];

      return pool
        .req(relays, [
          {
            kinds: NSITE_MANIFEST_KINDS,
            authors,
            limit: 50,
          },
        ])
        .pipe(
          onlyEvents(),
          mapEventsToStore(store),
          mapEventsToTimeline(),
        );
    },
    [pubkey, contactKey, relayKey, store, refreshKey],
  );

  // Fetch zap receipts for nsite events from contacts
  const zapEvents = use$(
    () => {
      if (!pubkey || relays.length === 0) return undefined;

      return pool
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .req(relays, [{ kinds: [9735], "#k": NSITE_MANIFEST_KINDS.map(String), limit: 50 } as any])
        .pipe(
          onlyEvents(),
          mapEventsToStore(store),
          mapEventsToTimeline(),
        );
    },
    [pubkey, contactKey, relayKey, store, refreshKey],
  );

  // Mark loading as done when events arrive
  useEffect(() => {
    if (manifestEvents !== undefined) {
      setIsLoading(false);
    }
  }, [manifestEvents]);

  // Combine and sort events
  const items = useMemo<FeedItem[]>(() => {
    const result: FeedItem[] = [];
    const seenEventIds = new Set<string>();

    // Add manifest events
    if (manifestEvents) {
      for (const event of manifestEvents) {
        if (!seenEventIds.has(event.id)) {
          seenEventIds.add(event.id);
          result.push({
            key: `manifest-${event.id}`,
            event,
            sourceKind: "manifest",
            timestamp: event.created_at,
          });
        }
      }
    }

    // Add zap events - extract the referenced nsite event
    if (zapEvents) {
      for (const zapReceipt of zapEvents) {
        // zap receipt has 'e' tag pointing to the zapped event
        const zappedEventId = zapReceipt.tags.find(([t]) => t === "e")?.[1];
        // The zap request (embedded in description tag) has the sender
        let zapperPubkey: string | undefined;
        try {
          const descriptionTag = zapReceipt.tags.find(([t]) => t === "description")?.[1];
          if (descriptionTag) {
            const zapRequest = JSON.parse(descriptionTag) as { pubkey?: string };
            zapperPubkey = zapRequest.pubkey;
          }
        } catch {}
        if (!zapperPubkey) {
          // fall back to the 'P' tag (sender pubkey)
          zapperPubkey = zapReceipt.tags.find(([t]) => t === "P")?.[1];
        }

        // Check if we have the original event in store
        if (zappedEventId) {
          const originalEvent = store.getEvent(zappedEventId);
          if (originalEvent && NSITE_MANIFEST_KINDS.includes(originalEvent.kind)) {
            const key = `zap-${zapReceipt.id}`;
            if (!seenEventIds.has(key)) {
              seenEventIds.add(key);
              result.push({
                key,
                event: originalEvent,
                sourceKind: "zap",
                zapperPubkey,
                timestamp: zapReceipt.created_at,
              });
            }
          }
        }
      }
    }

    // Sort by timestamp (newest first)
    result.sort((a, b) => b.timestamp - a.timestamp);

    return result;
  }, [manifestEvents, zapEvents, store]);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setIsLoading(true);
  }, []);

  return { items, isLoading, refresh };
}

// Public feed (no login) - show global nsite events
function usePublicNsiteFeed(): { items: FeedItem[]; isLoading: boolean; refresh: () => void } {
  const store = useEventStore();
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const relays = use$(nsiteRelays) ?? [];
  const relayKey = useMemo(() => JSON.stringify(relays), [relays]);

  const manifestEvents = use$(
    () => {
      if (relays.length === 0) return undefined;
      return pool
        .req(relays, [
          {
            kinds: NSITE_MANIFEST_KINDS,
            limit: 30,
          },
        ])
        .pipe(
          onlyEvents(),
          mapEventsToStore(store),
          mapEventsToTimeline(),
        );
    },
    [relayKey, store, refreshKey],
  );

  useEffect(() => {
    if (manifestEvents !== undefined) {
      setIsLoading(false);
    }
  }, [manifestEvents]);

  const items = useMemo<FeedItem[]>(() => {
    if (!manifestEvents) return [];
    return manifestEvents.map((event) => ({
      key: `manifest-${event.id}`,
      event,
      sourceKind: "manifest" as const,
      timestamp: event.created_at,
    }));
  }, [manifestEvents]);

  const refresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setIsLoading(true);
  }, []);

  return { items, isLoading, refresh };
}

export default function Index() {
  useSeoMeta({
    title: "nsite.feed — Nostr Static Sites",
    description: "Discover and explore static websites published on Nostr by your social graph.",
  });

  const account = useActiveAccount();
  const [gateway, setGateway] = useNsiteGateway();
  const [activeTab, setActiveTab] = useState<"friends" | "global">("friends");

  const friendFeed = useFriendNsiteFeed(account?.pubkey);
  const globalFeed = usePublicNsiteFeed();

  const activeFeed = account ? (activeTab === "friends" ? friendFeed : globalFeed) : globalFeed;

  return (
    <div className="min-h-screen bg-background">
      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden isolate">
        <div className="absolute -top-64 -left-32 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl -z-10" />
        <div className="absolute top-1/3 -right-64 w-[500px] h-[500px] rounded-full bg-primary/3 blur-3xl -z-10" />
      </div>

      <Header
        gateway={gateway}
        onGatewayChange={setGateway}
        feedItemCount={activeFeed.items.length}
        isLoading={activeFeed.isLoading}
        onRefresh={activeFeed.refresh}
      />

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Hero / intro (only shown when not logged in) */}
        {!account && (
          <div className="relative rounded-2xl overflow-hidden border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background p-8 text-center space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs text-primary font-medium mb-2">
              <Radio className="w-3 h-3 animate-pulse" />
              Live from the Nostr network
            </div>
            <h2 className="text-3xl font-bold gradient-text">
              Websites on Nostr
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto text-sm leading-relaxed">
              Discover and explore static websites published by your Nostr social graph.
              Login to see what your friends are building and zap your favorites.
            </p>
            <div className="flex justify-center pt-2">
              <LoginArea className="max-w-xs" />
            </div>
          </div>
        )}

        {/* Tab switcher (only when logged in) */}
        {account && (
          <div className="flex items-center gap-1 p-1 rounded-xl bg-muted/50 border border-border/40 w-fit">
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-4 text-sm rounded-lg transition-all",
                activeTab === "friends"
                  ? "bg-background text-foreground shadow-sm border border-border/40"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setActiveTab("friends")}
            >
              <Users className="h-3.5 w-3.5 mr-1.5" />
              Friends
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                "h-8 px-4 text-sm rounded-lg transition-all",
                activeTab === "global"
                  ? "bg-background text-foreground shadow-sm border border-border/40"
                  : "text-muted-foreground hover:text-foreground",
              )}
              onClick={() => setActiveTab("global")}
            >
              <Globe className="h-3.5 w-3.5 mr-1.5" />
              Global
            </Button>
          </div>
        )}

        {/* Feed content */}
        {activeFeed.isLoading ? (
          <LoadingSkeleton />
        ) : activeFeed.items.length === 0 ? (
          <EmptyState isLoggedIn={!!account} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeFeed.items.map((item, idx) => (
              <div
                key={item.key}
                style={{ animationDelay: `${Math.min(idx * 40, 400)}ms` }}
                className="animate-fade-in-up"
              >
                <NsiteCard
                  event={item.event}
                  gateway={gateway}
                  sourceKind={item.sourceKind}
                  zapperPubkey={item.zapperPubkey}
                />
              </div>
            ))}
          </div>
        )}

        {/* Load more placeholder */}
        {activeFeed.items.length > 0 && (
          <div className="flex justify-center pt-4">
            <p className="text-xs text-muted-foreground/60">
              Showing the {activeFeed.items.length} most recent nsite events
            </p>
          </div>
        )}

        {/* Footer */}
        <Separator className="mt-12 bg-border/40" />
        <footer className="flex items-center justify-between text-xs text-muted-foreground/60 pb-8">
          <span>
            Powered by{" "}
            <a
              href="https://nostr.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary transition-colors"
            >
              Nostr
            </a>
            {" "}& NIP-5A
          </span>
          <a
            href="https://shakespeare.diy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-primary transition-colors"
          >
            Vibed with Shakespeare
          </a>
        </footer>
      </main>
    </div>
  );
}
